import * as Sentry from "@sentry/browser"
import Pusher, { type Options as PusherOptions } from "pusher-js"
import { debugLogInDev } from "./util/util"
import type { Config } from "@ironzion/frontend-js-config"

type PusherSingleton =
  | {
      initialized: true
      client: Pusher
      auth: Auth
    }
  | {
      initialized: false
    }

type Auth = {
  jwt: string | null
}

type PusherError = {
  message: string
  type?: unknown
  data?: {
    code?: number
    message?: unknown
  }
  error?: {
    type?: unknown
    data?: {
      code?: number
      message?: unknown
    }
  }
}

type ConnectionState = "initialized" | "connecting" | "connected" | "unavailable" | "failed" | "disconnected"

const pusher: PusherSingleton = { initialized: false } as PusherSingleton

let pusherConnectionPromise = Promise.resolve()

function headersProvider() {
  if (pusher.initialized && pusher.auth.jwt) {
    return {
      Authorization: pusher.auth.jwt,
    }
  }

  return {}
}

const setClient = (pusherClient: Pusher) => {
  const initializedClient: {
    initialized: true
    client: Pusher
    auth: Auth
  } = {
    initialized: true,
    client: pusherClient,
    auth: {
      jwt: null,
    },
  }

  void Object.assign(pusher, initializedClient)
}

export const initPusher = (config: Config) => {
  Pusher.logToConsole = config.pusherLogToConsole

  if (pusher.initialized) {
    return
  }

  const newPusherClient = createPusherClient(config)

  setClient(newPusherClient)
}

export const disconnectPusher = async () => {
  if (!pusher.initialized) {
    return
  }

  const client = pusher.client

  return new Promise<void>((resolve, reject) => {
    try {
      const channels = client.allChannels()

      for (const channel of channels) {
        channel.unbind_all()
        channel.unsubscribe()
      }

      client.connection.bind("disconnected", () => {
        const disconnectedClient = client.unbind_all()
        setClient(disconnectedClient)

        deauthenticate()

        Sentry.addBreadcrumb({
          category: "pusher",
          message: "Pusher client disconnected",
          level: "info",
        })

        resolve()
      })

      client.disconnect()
    } catch (error) {
      Sentry.captureException(error)
      resolve()
    }
  })
}

const reconnectPusher = async (client: Pusher, config: Config, jwt: string) => {
  const connectionState = client.connection.state as ConnectionState

  switch (connectionState) {
    case "unavailable":
    case "connected":
    case "failed":
      return
    case "connecting":
    case "disconnected":
    case "initialized":
      return new Promise<void>((resolve, reject) => {
        client.connection.bind("connected", () => {
          client.connection.unbind("connected")
          client.connection.unbind("disconnected")

          Sentry.addBreadcrumb({
            category: "pusher",
            message: "Pusher client reconnected",
            level: "info",
          })

          resolve()
        })

        client.connection.bind("disconnected", () => {
          client.connection.unbind("connected")
          client.connection.unbind("disconnected")
          reject(new Error("failed to reconnect pusher client"))
        })

        authenticate(jwt)
        client.connect()
        listenForErrors(client, config)
      })
    default:
      return
  }
}

export const resetPusher = async (config: Config, jwt: string): Promise<void> => {
  if (!pusher.initialized) {
    return
  }

  Sentry.addBreadcrumb({
    category: "pusher",
    message: "Pusher reset started",
    level: "info",
  })

  pusherConnectionPromise = disconnectPusher().then(
    async () => void (await reconnectPusher(pusher.client, config, jwt)),
  )

  await pusherConnectionPromise
}

// If we have a jwt, create a pusher client that will authenticate private channels
const createPusherClient = (config: Config, jwt: string | null = null) => {
  if (pusher.initialized) {
    return pusher.client
  }

  const options: PusherOptions = {
    cluster: config.pusherCluster,
    // https://pusher.com/docs/channels/using_channels/connection/#tls-connections
    forceTLS: true,
    channelAuthorization: {
      transport: "ajax",
      endpoint: config.pusherAuthUrl,
      headersProvider,
    },
  }

  if (jwt) {
    authenticate(jwt)
  }

  const newPusher = new Pusher(config.pusherKey, options)

  listenForErrors(newPusher, config)

  return newPusher
}

const authenticate = (jwt: string) => {
  if (pusher.initialized) {
    pusher.auth.jwt = jwt
  }
}

const deauthenticate = () => {
  if (pusher.initialized) {
    pusher.auth.jwt = null
  }
}

/**
 * Subscribes to a channel once, checking for an existing subscription before attempting to establish a new one.
 *
 * If an error is thrown, it's caught and reported to Sentry, and `null` is returned.
 *
 * @param config - Config config
 * @param name  - Channel name
 */
export const subscribeToPusherChannel = async (config: Config, name: string) => {
  const client = createPusherClient(config)

  Sentry.addBreadcrumb({
    category: "pusher",
    message: `Subscription to channel requested: ${name}`,
    level: "info",
  })

  // Ensure anything that's waiting for the pusher client to finish connecting actually finishes
  await pusherConnectionPromise

  try {
    const existingChannel = client.channel(name)

    if (existingChannel) {
      debugLogInDev(`Returning existing subscription to pusher channel: '${name}'`)
      return existingChannel
    }

    const newChannel = client.subscribe(name)

    newChannel.bind("pusher:subscription_succeeded", () => {
      debugLogInDev(`Successfully subscribed to pusher channel: '${name}'`)

      Sentry.addBreadcrumb({
        category: "pusher",
        message: `Subscription to channel succeeded: ${name}`,
        level: "info",
      })
    })

    newChannel.bind("pusher:subscription_error", debugLogInDev)

    return newChannel
  } catch (error) {
    debugLogInDev(error)
    Sentry.captureException(error, { extra: { channelName: name } })
    return null
  }
}

const listenForErrors = (client: Pusher, config: Config) => {
  client.connection.bind("error", (pusherError: PusherError) => {
    if (config.pusherLogToConsole) {
      console.error(pusherError)
    }

    if (typeof Sentry !== "undefined") {
      // Pusher errors can be very low level and noisy and many are not
      // interesting to us as a notification in sentry.
      //
      // See https://pusher.com/docs/channels/library_auth_reference/pusher-websockets-protocol/#error-codes
      // and https://pusher.com/docs/channels/using_channels/events/#pushersubscription_error-1995773905
      //
      // These are the types of events we want to be notified about:
      //
      // - 4000-4099 Indicates an error resulting in the connection being closed
      //   by Pusher Channels, and that attempting to reconnect using the same
      //   parameters **will not succeed**.
      //   We saw this when config in the codebase got out of sync with config
      //   in pusher.
      //
      //     {
      //         "type": "WebSocketError",
      //         "error": {
      //             "type": "PusherError",
      //             "data": {
      //                 "code": 4001,
      //                 "message": "App key XXXXXXXXXXXXXXXXXXXX not in this cluster. Did you forget to specify the cluster?"
      //             }
      //         }
      //     }
      //
      // - Channel authorization errors, /pusher/auth endpoint returned incorrect
      //   information
      //
      //     {
      //         "type": "PusherError",
      //         "data": {
      //             "code": null,
      //             "message": "Auth info required to subscribe to private-org;XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
      //         }
      //     }

      // The errors come in many different shapes, here below we try to extract
      // data from the different possible formats they appear in.
      const code = pusherError?.data?.code || pusherError?.error?.data?.code
      const message = pusherError?.message || pusherError?.data?.message || pusherError?.error?.data?.message
      const innerType = pusherError?.error?.type
      const outerType = pusherError?.type
      // Either "PusherError" or in the cases with nested `type`,
      // "PusherError WebSocketError"
      const type = innerType ? `${innerType} ${outerType}` : (outerType as string)

      // If the error has a code, and is outside of 4000-4099, we are not
      // interested. See comment above.
      if (code && (code < 4000 || code > 4099)) {
        return
      }
      // If the error has no message, we are not interested.
      if (message === null) {
        return
      }

      const error: Error = new Error(`${code ? `[${code}] ` : ""}${message}`)
      error.name = type

      let pusherErrorStr: string
      try {
        pusherErrorStr = JSON.stringify(pusherError, null, 4)
      } catch (e) {
        pusherErrorStr = pusherError.message
      }

      // Unclear what this is, but it's pure noise
      if (pusherErrorStr.includes("Expected HMAC SHA256 hex digest")) {
        return
      }

      /**
       * https://vendrinc.sentry.io/issues/5239277588/?project=6558742
       * Unclear why this error is happening. It doesn't have a code, or a message.
       * But, based on its shape (visible in the Sentry error), it seems to be the same
       * or related to what this issue is tracking https://github.com/pusher/pusher-js/issues/743
       *
       * From what's mentioned in that issue, it may be related to network instability while
       * trying to keep an open websocket connection.
       */
      if (
        pusherError?.type === "WebSocketError" &&
        (pusherError?.error as Record<string, unknown>)?.isTrusted === true
      ) {
        return
      }

      Sentry.captureException(error, { extra: { error: pusherErrorStr } })
    }
  })
}
